import { useEffect, useRef, useState } from "react";
import { AeonyxIcon } from "./Logo";

const MODEL_URL =
  "https://customer-assets.emergentagent.com/job_peptide-protection/artifacts/efp44sq0_L_medicine%20box.glb";

// Silence model-viewer's transient "Failed to fetch" noise on unload
if (typeof window !== "undefined" && !window.__aeonyxMvSilencer) {
  window.__aeonyxMvSilencer = true;
  window.addEventListener("unhandledrejection", (e) => {
    const msg = String(e?.reason?.message || e?.reason || "");
    if (msg.includes("Failed to fetch") && msg.includes("model-viewer")) e.preventDefault();
  });
}

export default function HeroModel() {
  const mvRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      if (window.customElements && window.customElements.get("model-viewer")) {
        setReady(true);
      } else {
        setTimeout(check, 200);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    const el = mvRef.current;
    if (!el) return;
    const onProgress = (e) => {
      const p = (e.detail && e.detail.totalProgress) || 0;
      setProgress(Math.round(p * 100));
    };
    el.addEventListener("progress", onProgress);
    return () => el.removeEventListener("progress", onProgress);
  }, [ready]);

  const loaded = progress >= 100;

  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      data-testid="hero-3d-model"
    >
      {/* Neutral branded loading placeholder — visible until the 3D model finishes downloading */}
      <div
        className="absolute w-[62%] aspect-[4/3] rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 flex flex-col items-center justify-center gap-4 float-y pointer-events-none"
        style={{
          opacity: loaded ? 0 : 1,
          transition: "opacity 500ms ease-out",
        }}
      >
        <div className="relative">
          <span className="absolute inset-0 rounded-full bg-teal-brand/25 blur-2xl pulse-dot" />
          <AeonyxIcon className="relative h-16 w-auto opacity-80" />
        </div>
        <span className="font-mono-data text-[10px] uppercase tracking-[0.28em] text-white/60">
          Loading CryoVault
        </span>
      </div>

      {ready && (
        <model-viewer
          ref={mvRef}
          src={MODEL_URL}
          alt="CryoVault portable peptide fridge — interactive 3D model"
          camera-controls=""
          auto-rotate=""
          auto-rotate-delay="0"
          rotation-per-second="18deg"
          interaction-prompt="none"
          shadow-intensity="1.1"
          shadow-softness="0.9"
          exposure="1.05"
          environment-image="neutral"
          camera-orbit="20deg 78deg auto"
          min-camera-orbit="auto 0deg auto"
          max-camera-orbit="auto 180deg auto"
          disable-zoom=""
          touch-action="pan-y"
          loading="eager"
          reveal="auto"
          style={{
            width: "100%",
            height: "100%",
            background: "transparent",
            position: "absolute",
            inset: 0,
            opacity: loaded ? 1 : 0,
            transition: "opacity 600ms ease-in",
          }}
        />
      )}

      {/* Loading progress pill */}
      {progress > 0 && progress < 100 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 rounded-full bg-white/10 backdrop-blur-md border border-white/15 px-3 py-1.5 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-teal-brand pulse-dot" />
          <span className="font-mono-data text-[10px] uppercase tracking-widest text-white/85">
            Loading 3D · {progress}%
          </span>
        </div>
      )}
    </div>
  );
}
